import { create } from 'zustand';
import { authAPI, userAPI } from '../lib/api';
import { initializeSocket, disconnectSocket } from '../lib/realtime';

interface User {
  id: string;
  username: string;
  display_name: string | null;
  avatar: string | null;
  is_admin?: boolean;
}

interface Profile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  status: string | null;
  custom_status: string | null;
  bio: string | null;
  banner_url: string | null;
  created_at: string;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
  startOIDCLogin: () => Promise<void>;
  handleOIDCCallback: (code: string, state: string) => Promise<void>;
  signOut: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: any) => Promise<void>;
  setOnlineStatus: (status: string) => void;
}

// PKCE helpers
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// OIDC configuration (matches server config defaults)
const OIDC_AUTHORIZATION_ENDPOINT = 'https://accounts.epivalent.com/auth';
const OIDC_CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID || 'erozion';
const OIDC_SCOPES = 'openid gaming_profile profile email offline_access';
export const POST_LOGIN_REDIRECT_KEY = 'erozion_post_login_redirect';

function getRedirectUri(): string {
  if (window.api?.isElectron) {
    return import.meta.env.VITE_ELECTRON_REDIRECT_URI || 'https://dev.erozion.net/auth/appcallback';
  }
  return import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/auth/callback`;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  isLoading: true,
  error: null,

  startOIDCLogin: async () => {
    try {
      set({ error: null });

      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateRandomString(32);

      // Store PKCE values for callback (use localStorage so they survive in Electron)
      localStorage.setItem('oidc_code_verifier', codeVerifier);
      localStorage.setItem('oidc_state', state);

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: OIDC_CLIENT_ID,
        redirect_uri: getRedirectUri(),
        scope: OIDC_SCOPES,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state,
      });

      const authUrl = `${OIDC_AUTHORIZATION_ENDPOINT}?${params.toString()}`;

      if (window.api?.openOIDCLogin) {
        // Electron: open embedded login window; callback returns via IPC
        window.api.openOIDCLogin(authUrl);
      } else {
        // Web: redirect current window
        window.location.href = authUrl;
      }
    } catch (error: any) {
      console.error('OIDC login error:', error);
      set({ error: 'Failed to initiate login' });
    }
  },

  handleOIDCCallback: async (code: string, state: string) => {
    try {
      set({ isLoading: true, error: null });

      // Verify state
      const storedState = localStorage.getItem('oidc_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter — possible CSRF attack');
      }

      const codeVerifier = localStorage.getItem('oidc_code_verifier');
      if (!codeVerifier) {
        throw new Error('Missing PKCE code verifier');
      }

      // Clean up PKCE storage
      localStorage.removeItem('oidc_code_verifier');
      localStorage.removeItem('oidc_state');

      // Exchange code for tokens via the backend
      const data = await authAPI.callback(code, codeVerifier, getRedirectUri());

      // Store access token and user ID
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user_id', data.user.id);
      if (data.user.is_admin) localStorage.setItem('is_admin', '1');
      else localStorage.removeItem('is_admin');

      set({
        user: data.user,
      });

      await get().fetchProfile();

      // Initialize socket connection
      const socket = initializeSocket();
      if (socket) {
        console.log('Socket initialized after OIDC login');
      }

      const postLoginRedirect = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (postLoginRedirect) {
        localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        window.location.replace(postLoginRedirect);
      }
    } catch (error: any) {
      console.error('OIDC callback error:', error);
      set({
        error: error.response?.data?.message || error.message || 'Authentication failed',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });

      const userId = localStorage.getItem('user_id');
      if (userId) {
        try {
          await authAPI.logout(userId);
        } catch (e) {
          // Best effort logout
        }
      }

      localStorage.removeItem('access_token');
      localStorage.removeItem('user_id');
      disconnectSocket();

      set({ user: null, profile: null });
    } catch (error: any) {
      console.error('Sign out error:', error);
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user) return;

    try {
      set({ isLoading: true, error: null });
      const profile = await userAPI.getProfile();
      set({ profile });
    } catch (error: any) {
      console.error('Fetch profile error:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to fetch profile',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  updateProfile: async (updates) => {
    const { user } = get();
    if (!user) return;

    try {
      set({ isLoading: true, error: null });
      const updatedProfile = await userAPI.updateProfile(updates);
      // Merge returned profile with existing to avoid wiping fields
      set((state) => ({ profile: { ...state.profile, ...updatedProfile } }));
    } catch (error: any) {
      console.error('Update profile error:', error);
      set({
        error: error.response?.data?.message || error.message || 'Failed to update profile',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  setOnlineStatus: (status) => {
    const { user } = get();
    if (!user) return;
    set((state) => ({
      profile: state.profile ? { ...state.profile, status } : null,
    }));
  },
}));

// Check for existing access token on startup
const accessToken = localStorage.getItem('access_token');
const userId = localStorage.getItem('user_id');
if (accessToken && userId) {
  // Validate token by fetching profile
  userAPI
    .getProfile()
    .then((profile) => {
      useAuthStore.setState({
        user: {
          id: profile.id,
          username: profile.username,
          display_name: profile.display_name,
          avatar: profile.avatar_url,
        },
        profile,
        isLoading: false,
      });

      const socket = initializeSocket();
      if (socket) {
        console.log('Socket initialized on startup');
      }
    })
    .catch(() => {
      // Token invalid or expired — try refresh
      authAPI
        .refresh(userId)
        .then((data) => {
          localStorage.setItem('access_token', data.access_token);
          return userAPI.getProfile();
        })
        .then((profile) => {
          useAuthStore.setState({
            user: {
              id: profile.id,
              username: profile.username,
              display_name: profile.display_name,
              avatar: profile.avatar_url,
            },
            profile,
            isLoading: false,
          });
          initializeSocket();
        })
        .catch(() => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_id');
          useAuthStore.setState({ isLoading: false });
        });
    });
} else {
  useAuthStore.setState({ isLoading: false });
}
